import { Transaction } from '@square/dashboard-protos/models/squareup/sfs/models/transaction';
import moment from 'moment-timezone';
import { TransferType } from '@square/dashboard-protos/models/squareup/sfs/models/transfer';
const COMMON_DEPOSIT = {
  labelKey: 'banking.shared.activity.labels.deposit',
  svgName: '24px-coin-deposit-stroked',
  svgModifierClass: 'banking-activity__icon-container--success',
  willEarnInterest: true
};
const COMMON_WITHDRAW = {
  labelKey: 'banking.shared.activity.labels.withdrawal',
  svgName: '24px-atm-withdraw-stroked',
  svgModifierClass: 'banking-activity__icon-container--critical',
  willEarnInterest: false
};

// CorePro documentation for the transaction object (mostly passed through by our API):
// https://docs.corepro.io/api/transaction/index
//
// CorePro documentation for transaction types:
// https://docs.corepro.io/api/transactiontype
// eslint-disable-next-line complexity
export function fromSFSTransaction(transaction, intl, features, details) {
  if (!transaction.amount) {
    throw new Error(`transaction ${transaction.id} has no amount`);
  }
  const availableAt = moment(transaction.availableAt);
  let isPending = transaction.status === Transaction.Status.PENDING || transaction.status === Transaction.Status.INITIATED;
  const createdAt = new Date(transaction.createdAt);
  let isAvailable = false;
  let isSettled = false;
  const isInstant = transaction.createdAt === transaction.settledAt && transaction.createdAt === transaction.availableAt;
  const isVoided = transaction.status === Transaction.Status.VOIDED;

  // Treat a SETTLED transaction as PENDING if it is not yet available
  // Still use isSettled to determine whether it is earning interest
  if (transaction.status === Transaction.Status.SETTLED) {
    isSettled = true;
    if (!availableAt.isValid() || availableAt.isAfter(moment())) {
      isPending = true;
    } else {
      isAvailable = true;
    }
  }
  let labelKey;
  let svgName = '16px-question-circle-stroked';
  let svgModifierClass = '';
  let willEarnInterest = false;
  let fromAccountName = null;
  let toAccountName = null;
  const transactionType = transaction.type;
  switch (transactionType) {
    case 'CPDEP':
    case 'CPBDEP':
      ({
        labelKey,
        svgName,
        svgModifierClass,
        willEarnInterest
      } = COMMON_DEPOSIT);
      break;
    case 'CPWTH':
    case 'CPBWTH':
      ({
        labelKey,
        svgName,
        svgModifierClass,
        willEarnInterest
      } = COMMON_WITHDRAW);
      break;
    case 'CPXFR':
      if (transaction.amount.amount > 0) {
        ({
          labelKey,
          svgName,
          svgModifierClass,
          willEarnInterest
        } = COMMON_DEPOSIT);
      } else {
        ({
          labelKey,
          svgName,
          svgModifierClass,
          willEarnInterest
        } = COMMON_WITHDRAW);
      }
      break;
    case 'INTPD':
      labelKey = 'banking.shared.activity.labels.interest';
      svgName = '24px-gold-stack-stroked';
      svgModifierClass = 'banking-activity__icon-container--warning';
      willEarnInterest = false;
      break;
    case 'INTADJ':
      labelKey = 'banking.shared.activity.labels.interestAdjustment';
      svgName = '24px-gold-stack-stroked';
      svgModifierClass = 'banking-activity__icon-container--warning';
      willEarnInterest = false;
      break;
    case 'RSVDEP':
    case 'CTCRD':
      if (transaction.savingsTransferType === TransferType.AUTOMATED_SAVINGS) {
        labelKey = 'banking.shared.activity.labels.automaticDeposit';
        svgName = '24px-arrow-cycle-stroked';
        svgModifierClass = 'banking-activity__icon-container--emphasis';
        willEarnInterest = true;
      } else {
        ({
          labelKey,
          svgName,
          svgModifierClass,
          willEarnInterest
        } = COMMON_DEPOSIT);
      }
      break;
    case 'RSVWTH':
    case 'CTDBT':
      ({
        labelKey,
        svgName,
        svgModifierClass,
        willEarnInterest
      } = COMMON_WITHDRAW);
      break;
    case 'UNKDEP':
      ({
        labelKey,
        svgName,
        svgModifierClass,
        willEarnInterest
      } = COMMON_DEPOSIT);
      break;
    case 'UNKWTH':
      ({
        labelKey,
        svgName,
        svgModifierClass,
        willEarnInterest
      } = COMMON_WITHDRAW);
      break;
    case 'RTNDEP':
    case 'RTNWTH':
      svgName = '16px-alert-circle-stroked';
      labelKey = 'banking.shared.activity.labels.return';
      break;
    default:
      labelKey = 'banking.shared.activity.labels.unknown';
      break;
  }
  const isUnavailableAndEarningInterest = willEarnInterest && transaction.status === Transaction.Status.SETTLED && !isAvailable;
  let subtitle = '';
  if (!features.isEnabled('sfscore/batch_automated_transactions')) {
    subtitle = intl.formatDate(createdAt, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  if (features.isEnabled('sfscore/batch_automated_transactions') && transaction.savingsTransferType === TransferType.AUTOMATED_SAVINGS) {
    subtitle = transaction.description;
  } else if (isPending) {
    subtitle = intl.t('banking.shared.activity.initiated');
    svgName = '24px-clock-stroked';
    svgModifierClass = '';
  }
  if (isUnavailableAndEarningInterest) {
    subtitle = intl.t('banking.shared.activity.earningInterest');
    svgName = '24px-coin-deposit-stroked';
    svgModifierClass = '';
  }
  if (isVoided) {
    svgName = '24px-deny-stroked';
    svgModifierClass = '';
  }
  if (details && transaction.savingsTransferType !== TransferType.AUTOMATED_SAVINGS) {
    toAccountName = details.toAccountName;
    fromAccountName = details.fromAccountName;
  }
  const activity = {
    activities: [],
    amount: transaction.amount,
    availableAt: new Date(transaction.availableAt),
    balance: null,
    createdAt,
    description: transaction.description,
    isInstant,
    fromAccountName,
    id: transaction.id,
    image: null,
    isAvailable,
    isBundle: false,
    isSettled,
    isPending,
    isVoided,
    settledAt: new Date(transaction.settledAt),
    state: null,
    subtitle,
    svgName,
    svgModifierClass,
    toAccountName,
    traceId: transaction.id,
    transactionToken: null,
    title: intl.t(labelKey),
    willEarnInterest,
    isUnavailableAndEarningInterest,
    transferType: transaction.savingsTransferType
  };
  return activity;
}
export function getStateTranslation(activity) {
  if (activity.isUnavailableAndEarningInterest) {
    return 'banking.checking.unifiedActivity.activityState.earningInterest';
  } else if (activity.isPending) {
    return 'banking.checking.unifiedActivity.activityState.initiated';
  } else {
    return 'banking.checking.unifiedActivity.activityState.settled';
  }
}
export function buildLineItems(activity, intl) {
  const lineItems = [];
  if (activity.toAccountName) {
    lineItems.push({
      description: {
        label: intl.t('banking.shared.activity.addTo'),
        value: activity.toAccountName
      }
    });
  }
  if (activity.fromAccountName) {
    lineItems.push({
      description: {
        label: intl.t('banking.shared.activity.transferOutOf'),
        value: activity.fromAccountName
      }
    });
  }
  if (activity.isInstant) {
    lineItems.push({
      timestamp: {
        label: intl.t('banking.shared.activity.completedOn'),
        value: intl.formatDate(activity.availableAt, {
          format: 'fullDateLong'
        })
      }
    });
  } else {
    lineItems.push({
      timestamp: {
        label: intl.t('banking.shared.activity.initiatedOn'),
        value: intl.formatDate(activity.createdAt, {
          format: 'fullDateLong'
        })
      }
    });
    if (activity.willEarnInterest) {
      lineItems.push({
        timestamp: {
          label: activity.isSettled ? intl.t('banking.shared.activity.earnedInterestOn') : intl.t('banking.shared.activity.earningInterestOn'),
          value: intl.formatDate(activity.settledAt, {
            format: 'fullDateLong'
          })
        }
      });
    }
    lineItems.push({
      timestamp: {
        label: activity.isAvailable ? intl.t('banking.shared.activity.completedOn') : activity.willEarnInterest ? intl.t('banking.shared.activity.availableOn') : intl.t('banking.shared.activity.willBeCompletedOn'),
        value: intl.formatDate(activity.availableAt, {
          format: 'fullDateLong'
        })
      }
    });
  }
  lineItems.push({
    description: {
      label: intl.t('banking.shared.activity.traceId'),
      value: activity.traceId
    }
  });
  if (activity.description) {
    lineItems.push({
      description: {
        label: intl.t('banking.shared.activity.description'),
        value: activity.description,
        isLong: true
      }
    });
  }
  return lineItems;
}
export function detailsFromSFSTransaction(transaction, intl, features, details) {
  if (!transaction.amount) {
    throw new Error(`transaction ${transaction.id} has no amount`);
  }
  const activity = fromSFSTransaction(transaction, intl, features, details);
  const detail = {
    id: activity.id,
    transactionToken: activity.id,
    title: activity.title,
    stateCategory: getStateTranslation(activity),
    amount: activity.amount,
    lineItems: buildLineItems(activity, intl),
    svgName: activity.svgName,
    svgModifierClass: activity.svgModifierClass,
    isPrintableCheck: false
  };
  return detail;
}